/**
 * Cliente del CRUD de Scout (búsquedas + candidatos) para el BO.
 * Ver docs/scout-plan/04.
 */
import { deleteJsonAdmin, get, patchJsonAdmin, postJsonAdmin } from './_core.js';

export interface ScoutRole {
  slug: string;
  title: string;
  language: string;
}

export interface ScoutSearch {
  id: string;
  title: string;
  roleSlug: string;
  voicePersonaSlug: string | null;
  /** Nombre con el que se presenta la entrevistadora (override por búsqueda). */
  interviewerName: string | null;
  publicToken: string;
  status: 'open' | 'closed';
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
}

export interface ScoutSearchWithCount extends ScoutSearch {
  candidateCount: number;
}

export interface ScoutScoreDimension {
  score: number;
  evidence?: string;
}

export interface ScoutResult {
  candidateId: string;
  voiceSessionId: string;
  scores: Record<string, ScoutScoreDimension>;
  recommendation: 'advance' | 'maybe' | 'reject';
  summary: string;
  confidence: 'high' | 'low';
  createdAt: string;
}

export interface ScoutCandidate {
  id: string;
  searchId: string;
  fullName: string;
  email: string;
  phone: string | null;
  consentAt: string;
  hasCv: boolean;
  status: 'started' | 'completed' | 'evaluated';
  voiceSessionId: string | null;
  createdAt: string;
  /** Resultado del scoring (Fase 3). Null hasta que el distiller corre. */
  result: ScoutResult | null;
}

export async function fetchScoutRoles(): Promise<ScoutRole[]> {
  const res = await get<ScoutRole[]>('/admin/scout/roles');
  return res.data;
}

export async function fetchScoutSearches(): Promise<ScoutSearchWithCount[]> {
  const res = await get<ScoutSearchWithCount[]>('/admin/scout/searches');
  return res.data;
}

export interface CreateScoutSearchInput {
  title: string;
  roleSlug: string;
  voicePersonaSlug?: string;
  interviewerName?: string;
  expiresAt?: string;
}

export async function postScoutSearch(input: CreateScoutSearchInput): Promise<ScoutSearch> {
  return postJsonAdmin<ScoutSearch>('/admin/scout/searches', input);
}

export interface UpdateScoutSearchInput {
  title?: string;
  roleSlug?: string;
  status?: 'open' | 'closed';
  voicePersonaSlug?: string | null;
  interviewerName?: string | null;
  expiresAt?: string | null;
}

export async function patchScoutSearch(
  id: string,
  input: UpdateScoutSearchInput,
): Promise<ScoutSearch> {
  return patchJsonAdmin<ScoutSearch>(`/admin/scout/searches/${id}`, input);
}

export async function deleteScoutSearch(id: string): Promise<{ id: string }> {
  return deleteJsonAdmin<{ id: string }>(`/admin/scout/searches/${id}`, {});
}

export async function fetchScoutSearchCandidates(
  id: string,
): Promise<{ search: ScoutSearch; candidates: ScoutCandidate[] }> {
  const res = await get<{ search: ScoutSearch; candidates: ScoutCandidate[] }>(
    `/admin/scout/searches/${id}/candidates`,
  );
  return res.data;
}

export interface ScoutCandidateDetail {
  search: ScoutSearch;
  /** Título legible del rol (de la guía `.md`); cae al título de la búsqueda. */
  roleTitle: string;
  candidate: ScoutCandidate;
}

export async function fetchScoutCandidate(id: string): Promise<ScoutCandidateDetail> {
  const res = await get<ScoutCandidateDetail>(`/admin/scout/candidates/${id}`);
  return res.data;
}
